import { FC, useEffect, useRef, useState } from "react";
import { FontIcon, Stack, Text } from "@fluentui/react";
import { useNavigate } from "react-router-dom";
import { useUploadNotification, UploadItem } from "../../context/UploadNotificationContext";
import { DocumentService } from "../../services/DocumentService";
import "./index.css";

type ProjectGroup = {
  projectRoute: string;
  projectId: number;
  projectName?: string;
  items: UploadItem[];
};

const isCompletedStage = (stage: string) => {
  const v = (stage || "").toLowerCase().trim();
  return v.includes("complete") || v.includes("success") || v === "done";
};

const NotificationPanel: FC = () => {
  const {
    uploads,
    isMinimized,
    completedNotifications,
    clearNotification,
    setIsMinimized,
    uploadRoute,
    recoveredUploads,
    setRecoveredUploads,
    setUploads,
    setUploadRoute,
    setOperationIds,
    setFileNameMap,
  } = useUploadNotification();

  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const hasActiveUpload = isMinimized && uploads.length > 0;

  // Group recovered uploads by project
  const projectGroups: ProjectGroup[] = Object.values(
    recoveredUploads.reduce<Record<string, ProjectGroup>>((acc, item) => {
      const key = item.projectRoute || "";
      if (!acc[key]) {
        acc[key] = {
          projectRoute: key,
          projectId: item.projectId ?? 0,
          projectName: item.projectName,
          items: [],
        };
      }
      acc[key].items.push(item);
      return acc;
    }, {})
  );

  const badgeCount =
    (hasActiveUpload ? 1 : 0) +
    projectGroups.length +
    completedNotifications.length;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCancelSessionUpload = async (opId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try { await DocumentService.cancelUpload(opId); } catch {}
    const remaining = uploads.filter((u) => u.fileOperationId !== opId);
    setUploads(remaining);
    if (remaining.length === 0) setIsMinimized(false);
  };

  const handleCancelRecoveredUpload = async (opId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try { await DocumentService.cancelUpload(opId); } catch {}
    setRecoveredUploads((prev) => prev.filter((u) => u.fileOperationId !== opId));
  };

  // Restore minimized modal (same-session)
  const handleRestoreModal = () => {
    if (uploadRoute) {
      navigate(uploadRoute, { state: { restoreUploadModal: true } });
    }
    setIsMinimized(false);
    setIsOpen(false);
  };

  // Restore a recovered project's uploads
  const handleRestoreRecovered = (group: ProjectGroup) => {
    setUploads(group.items);
    setUploadRoute(group.projectRoute);
    setOperationIds(group.items.map((u) => u.fileOperationId));
    setFileNameMap(
      Object.fromEntries(group.items.map((u) => [u.fileOperationId, u.fileName]))
    );
    setRecoveredUploads((prev) =>
      prev.filter((u) => u.projectId !== group.projectId)
    );
    setIsMinimized(false);
    navigate(group.projectRoute, { state: { restoreUploadModal: true } });
    setIsOpen(false);
  };

  const ProgressBars: FC<{
    items: UploadItem[];
    onCancel?: (opId: string, e: React.MouseEvent) => void;
  }> = ({ items, onCancel }) => (
    <>
      {items.map((u) => (
        <Stack key={u.fileOperationId} style={{ marginTop: "6px" }}>
          <Stack
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "3px",
            }}
          >
            <Text className="notif-file-name" title={u.fileName}>
              {u.fileName}
            </Text>
            <Text className="notif-percent">
              {isCompletedStage(u.stage) ? "✓" : `${u.percent}%`}
            </Text>
          </Stack>
          <Stack style={{ flexDirection: "row", alignItems: "center", gap: "4px" }}>
            <div className="notif-progress-track" style={{ flex: 1 }}>
              <div
                className="notif-progress-fill"
                style={{ width: `${Math.max(0, Math.min(100, u.percent))}%` }}
              />
            </div>
            {onCancel && !isCompletedStage(u.stage) && u.stage !== "cancelled" && (
              <FontIcon
                iconName="Cancel"
                className="notif-cancel-file"
                onClick={(e) => onCancel(u.fileOperationId, e)}
              />
            )}
          </Stack>
        </Stack>
      ))}
    </>
  );

  return (
    <div ref={panelRef} className="notif-wrapper">
      <div className="notif-bell" onClick={() => setIsOpen((o) => !o)}>
        <FontIcon iconName="Ringer" className="notif-bell-icon" />
        {badgeCount > 0 && (
          <span className="notif-badge">{badgeCount > 9 ? "9+" : badgeCount}</span>
        )}
      </div>

      {isOpen && (
        <div className="notif-panel">
          <Text className="notif-panel-title">Notifications</Text>

          {!hasActiveUpload &&
            projectGroups.length === 0 &&
            completedNotifications.length === 0 && (
              <Text className="notif-empty">No notifications</Text>
            )}

          {/* Current session — minimized modal */}
          {hasActiveUpload && (
            <div className="notif-item notif-upload" onClick={handleRestoreModal}>
              <Stack style={{ width: "100%" }}>
                <Text className="notif-item-title">Upload in progress</Text>
                <ProgressBars items={uploads} onCancel={handleCancelSessionUpload} />
                <Text className="notif-restore-hint">Click to restore</Text>
              </Stack>
            </div>
          )}

          {/* Recovered uploads — one group per project */}
          {projectGroups.map((group) => (
            <div
              key={group.projectRoute}
              className="notif-item notif-upload"
              onClick={() => handleRestoreRecovered(group)}
            >
              <Stack style={{ width: "100%" }}>
                <Text className="notif-item-title">
                  {group.projectName
                    ? `Uploading to ${group.projectName}`
                    : "Upload in progress"}
                </Text>
                <ProgressBars items={group.items} onCancel={handleCancelRecoveredUpload} />
                <Text className="notif-restore-hint">Click to restore</Text>
              </Stack>
            </div>
          ))}

          {/* Completed notifications */}
          {completedNotifications.map((n) => (
            <div key={n.id} className="notif-item notif-complete">
              <Stack style={{ flex: 1 }}>
                <Text className="notif-item-title">Upload complete</Text>
                <Text className="notif-file-list">{n.fileNames.join(", ")}</Text>
                <Text className="notif-timestamp">
                  {n.timestamp.toLocaleTimeString()}
                </Text>
              </Stack>
              <FontIcon
                iconName="Cancel"
                className="notif-close"
                onClick={(e) => {
                  e.stopPropagation();
                  clearNotification(n.id);
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
